import React from 'react';
import {QrCode, QrCodeData} from "types/QrCode";
import {FormikHelpers} from "formik";
import Form, {FormBlock, FormSection, TextField, ToggleField} from "components/Form/Form";
import {useOperator} from "context/OperatorContext";
import {HashLink as Link} from 'react-router-hash-link';
import {useQuery} from "react-query";
import ngage from "../../../services/ngage";
import IconName from "../../../types/IconName";
import {toTitleCase} from "../../../services/string";
import {Item} from "../../../components/Form/Selector/Selector";
import {BeaconData} from "../../../types/Beacon";
import Selector from "../../../components/Form/Selector";
import Button from "../../../components/Button";
import * as Yup from "yup";

// Types ----

type QrCodeFormProps = {
  qrCode?: QrCode,
  onSubmit: (values: QrCodeData, helpers: FormikHelpers<QrCodeData>) => any
}

// Component ----

function QrCodeForm({onSubmit, qrCode}: QrCodeFormProps) {
  const {settings} = useOperator();

  //TODO: Make a reusable topic selector component
  const {data: selectableTopics, isLoading: topicsLoading} = useQuery(['topics.all'], async () => {
    const topicSets = await Promise.all([
      ngage.exhibits.filtered({per_page: 1000}).then(page => page.data.map(exhibit => ({
        type: 'exhibit',
        id: `exhibit-${exhibit.id}`,
        name: exhibit.name,
        description: exhibit.type?.name ?? 'Exhibit',
        icon: IconName.LIBRARY
      }))),

      ngage.facilities.filtered({per_page: 1000}).then(page => page.data.map(facility => ({
        type: 'facility',
        id: `facility-${facility.id}`,
        name: facility.name,
        description: facility.type?.name ?? 'Facility',
        icon: IconName.OFFICE_BUILDING
      }))),

      ngage.trails.filtered({perPage: 1000}).then(page => page.data.map(trail => ({
        type: 'trail',
        id: `trail-${trail.id}`,
        name: trail.name,
        description: trail.type ? `${toTitleCase(trail.type)} Trail` : 'Trail',
        icon: IconName.MAP
      }))),
    ]);

    return topicSets.flat().sort((left, right) => left.name > right.name ? 1 : -1) as Item[];
  });

  function selectTopic(itemId: string, {setFieldValue}: FormikHelpers<BeaconData>) {
    const [type, id] = itemId.split('-')

    void setFieldValue('topic', {
      type: type,
      id: Number(id),
    })
  }

  function clearTopic({setFieldValue}: FormikHelpers<BeaconData>) {
    void setFieldValue('topic', null)
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
  })

  const initialValues: QrCodeData = {
    name: qrCode?.name || '',
    is_enabled: qrCode?.is_enabled ?? true,
    // Branding
    appearance: qrCode?.appearance ?? {
      is_branded: false,
      brand_size: 0.2
    },
    // Topic
    topic: qrCode?.topic ? {
      type: qrCode.topic.type,
      id: qrCode.topic.id
    } : null
  }

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <FormSection
        name="Basic Details"
        description="Identiying information about the QR code"
        columns={6}
      >
        <FormBlock columns={4}>
          <TextField
            name="name"
            label="Name *"
            hint='255 chars max'
            help="An internal name for this QR code"
            autoComplete="off"
            maxLength={255}
            autoFocus
          />
        </FormBlock>
      </FormSection>

      <FormSection
        name="Appearance"
        description="Customise how this QR code looks"
        columns={6}
      >
        <FormBlock columns={6}>
          {!!settings?.branding.qr_logo &&
            <ToggleField
              name="appearance.is_branded"
              label="Branded"
              description="Show a logo in the centre. (PNG only)."
              disabled={!settings?.branding.qr_logo}
            />
          }

          {!settings?.branding.qr_logo && (
            <>
              <span className="block text-sm font-medium leading-6 text-gray-900">
                Branding
              </span>
              <span className="text-sm text-gray-500">Before you can enable QR branding, you need to supply a QR logo.
                This can be done in <Link smooth to="/settings#branding" className="underline text-brand">branding settings.</Link>
              </span>
            </>
          )}
        </FormBlock>
      </FormSection>

      <FormSection
        name="Location"
        description="Customise where this QR code is used"
        columns={6}
      >
        <FormBlock columns={3}>
          {form => (
            <div className="flex space-x-2">
              <div className="w-full">
                <Selector
                  label={"Topic"}
                  isLoading={topicsLoading}
                  items={selectableTopics ?? []}
                  selectedId={form.values.topic ? `${form.values.topic.type}-${form.values.topic.id}` : null}
                  onChange={topic => selectTopic(String(topic.id), form)}
                />
                <p className="mt-2 text-sm text-gray-500">
                  (Optional) Choose the topic to associate with this QR code, if any
                </p>
              </div>
              <div className="mt-6">
                <Button color={Button.colors.SECONDARY} onClick={() => clearTopic(form)}>
                  Clear
                </Button>
              </div>
            </div>
          )}
        </FormBlock>
      </FormSection>

      <FormSection
        name="Status"
        description="Enable or disable this QR code"
        columns={6}
      >
        <FormBlock columns={6}>
          <ToggleField
            name="is_enabled"
            label="Enabled"
          />
        </FormBlock>
      </FormSection>
    </Form>
  );
}

export default QrCodeForm;